import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { adminApi } from "@/lib/adminAuth";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const empty = { slug: "", name: "", tokens: 100, price_try: 99, price_usd: 3.99, price_eur: 3.49, bonus_tokens: 0, is_popular: false, is_active: true, sort_order: 10 };

export default function AdminPackages() {
  const [pkgs, setPkgs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/admin/token-packages", adminApi()).then(({ data }) => setPkgs(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (creating) await api.post("/admin/token-packages", editing, adminApi());
      else await api.patch(`/admin/token-packages/${editing.id}`, editing, adminApi());
      toast.success("Kaydedildi");
      setEditing(null); setCreating(false);
      load();
    } catch (e) { toast.error("Hata"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/admin/token-packages/${id}`, adminApi());
    load();
  };

  return (
    <div className="font-admin-body">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-admin-head text-3xl">Jeton Paketleri</h1>
        <button onClick={() => { setCreating(true); setEditing({ ...empty }); }} data-testid="new-pkg-btn" className="admin-btn flex items-center gap-2"><Plus className="w-4 h-4" /> Yeni Paket</button>
      </div>

      <div className="admin-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-white/50 uppercase tracking-wider">
            <tr className="border-b border-[#27272A]">
              <th className="p-3">Slug</th><th className="p-3">Ad</th><th className="p-3">Jeton</th><th className="p-3">+Bonus</th><th className="p-3">₺</th><th className="p-3">$</th><th className="p-3">€</th><th className="p-3">Aktif</th><th></th>
            </tr>
          </thead>
          <tbody>
            {pkgs.map((p) => (
              <tr key={p.id} className="border-b border-[#18181B]">
                <td className="p-3 font-mono text-xs">{p.slug}</td>
                <td className="p-3">{p.name} {p.is_popular && <span className="text-[#F59E0B]">★</span>}</td>
                <td className="p-3 tabular-nums">{p.tokens}</td>
                <td className="p-3 tabular-nums">{p.bonus_tokens}</td>
                <td className="p-3 tabular-nums">{p.price_try}</td>
                <td className="p-3 tabular-nums">${p.price_usd}</td>
                <td className="p-3 tabular-nums">€{p.price_eur || 0}</td>
                <td className="p-3">{p.is_active ? "✓" : "✕"}</td>
                <td className="p-3 flex gap-1">
                  <button onClick={() => setEditing({ ...p })} className="admin-btn-ghost text-xs px-2 py-1">Düzenle</button>
                  <button onClick={() => remove(p.id)} className="admin-btn-ghost text-xs px-2 py-1 text-red-400"><Trash2 className="w-3 h-3" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4">
          <div className="admin-card p-6 w-full max-w-lg">
            <h2 className="font-admin-head text-xl mb-4">{creating ? "Yeni Paket" : "Paket Düzenle"}</h2>
            <div className="grid grid-cols-2 gap-3">
              {creating && (
                <div className="col-span-2">
                  <label className="text-xs text-white/50">Slug</label>
                  <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="admin-input" />
                </div>
              )}
              <div className="col-span-2">
                <label className="text-xs text-white/50">Ad</label>
                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="admin-input" />
              </div>
              <div>
                <label className="text-xs text-white/50">Jeton</label>
                <input type="number" value={editing.tokens} onChange={(e) => setEditing({ ...editing, tokens: parseInt(e.target.value) || 0 })} className="admin-input" />
              </div>
              <div>
                <label className="text-xs text-white/50">Bonus Jeton</label>
                <input type="number" value={editing.bonus_tokens} onChange={(e) => setEditing({ ...editing, bonus_tokens: parseInt(e.target.value) || 0 })} className="admin-input" />
              </div>
              <div>
                <label className="text-xs text-white/50">Fiyat (₺)</label>
                <input type="number" step="0.01" value={editing.price_try} onChange={(e) => setEditing({ ...editing, price_try: parseFloat(e.target.value) || 0 })} className="admin-input" />
              </div>
              <div>
                <label className="text-xs text-white/50">Fiyat ($)</label>
                <input type="number" step="0.01" value={editing.price_usd} onChange={(e) => setEditing({ ...editing, price_usd: parseFloat(e.target.value) || 0 })} className="admin-input" />
              </div>
              <div>
                <label className="text-xs text-white/50">Fiyat (€)</label>
                <input type="number" step="0.01" value={editing.price_eur || 0} onChange={(e) => setEditing({ ...editing, price_eur: parseFloat(e.target.value) || 0 })} className="admin-input" />
              </div>
              <div>
                <label className="text-xs text-white/50">Sıra</label>
                <input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value) || 0 })} className="admin-input" />
              </div>
              <div className="col-span-2 flex gap-4">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.is_popular} onChange={(e) => setEditing({ ...editing, is_popular: e.target.checked })} className="accent-[#F59E0B]" /> Popüler</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} className="accent-[#F59E0B]" /> Aktif</label>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={save} className="admin-btn flex-1">Kaydet</button>
              <button onClick={() => { setEditing(null); setCreating(false); }} className="admin-btn-ghost flex-1">İptal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
